// src/pages/expense/service/serviceDraftConfig.js
// ✅ 전 사업 확산 [4] — 용역사업의 businessConfig. 환경/종합과 동일한 공통 컴포넌트
// (pages/expense/_shared/ExpenseDraft*.jsx)에 이 config를 넘긴다.
//
// ⚠️ (구) 페이지 ExpenseServiceNew.jsx의 정교한 "인건비/일용직 빠른입력" 퀵플로우
// (팝업 5종 연쇄, forwardRef 기반 VendorPanel/SalaryPanel, 세금 자동계산)는 이번
// 통합 페이지로 흡수하지 않았다 — DB 실측(176건) 결과 그 퀵플로우가 만드는 형태의
// 데이터가 사실상 없고(일용직 관련은 8건뿐이며 SalaryPanel 표준 데이터 형태와도
// 다름), 지배적 패턴(149/176≈85%)은 사업장별 재료비/복리후생비/용역비/위탁 물품·
// 경비 구입이었다. 그래서 그 지배적 패턴에 맞춘 신규 템플릿(service_contract_payment)
// 과 SalaryPanel 호환 신규 템플릿(service_daily_payroll, 신규 SalaryPanelIntegrated
// 패널 사용)만 추가했다. (구) 페이지는 손대지 않고 그대로 병존.
//
// 기존 service_purchase(물품구매)/service_daily_wage(일용직급여)는 통합 페이지에
// 포함하지 않았다 — 둘 다 (구) 범용 2단계 흐름(DraftDocumentNew.jsx 등)에 실제
// 하드코딩 분기가 있는 살아있는 템플릿 정의라, 컬럼 구조를 표준 ExpenseItemPanel/
// SalaryPanel 형태로 바꾸면 그 (구) 흐름에 영향을 줄 위험이 있어 그대로 두고,
// 대신 이번 통합 페이지 전용으로 호환되는 신규 코드를 만들었다(DocumentTypeSelector.jsx
// 참고 — "expense_draft-용역사업" 키는 전 사업 확산 [4]에서 의도적으로 그대로 둠).
import { makeExpenseDraftConfig } from "../_shared/expenseDraftConfig";
import VendorPanel from "../_shared/VendorPanel";
import ExpenseItemPanel from "../_shared/ExpenseItemPanel";
import SalaryPanelIntegrated from "./SalaryPanelIntegrated";

// ── 6종 — Intl.Collator("ko") 정렬 검증 완료: 계약인지세/수수료(보건)/수수료(안전)/
// 용역대금 지급/유류비(세차비)/일용직급여 ──
const TEMPLATES = [
  { code: "service_contract", label: "계약인지세" },
  { code: "service_commission_health", label: "수수료(보건)" },
  { code: "service_commission_safety", label: "수수료(안전)" },
  { code: "service_contract_payment", label: "용역대금 지급" },
  { code: "fuel_cost", label: "유류비(세차비)" },
  { code: "service_daily_payroll", label: "일용직급여" },
];

// ✅ 종류 선택 시 자동 채워지는 구분/내용/분야 — ServiceExpense.service_type은
// 실측(176건) 결과 예산분류가 아니라 "사업장명"(예: 금나래문화체육센터(용역))을
// 담는 용도라 다른 사업(업무추진비 등 분류값)과 성격이 달라, 대부분 빈 값(자유
// 입력)으로 두고 값이 고정적인 일용직급여만 채운다(8건 전부 "일용직(용역사업)").
// ✅ 2026-07-30 — fuel_cost/service_daily_payroll의 category_name을 예산과목
// 마지막 항목과 일치시킴(대조표 조사 결과 반영). service_contract는 예산과목
// 필드 자체가 없어 비교 대상이 아니라 기존 값 유지.
const CATEGORY_ITEM_FIELD_DEFAULTS = {
  service_contract: { category_name: "공공운영비", item_name: "인지세", field_name: "지출" },
  fuel_cost: { category_name: "차량선박비", item_name: "유류비", field_name: "지출" },
  service_daily_payroll: { category_name: "급여", item_name: "", field_name: "지급" },
};

// ✅ 계약/구매성 — 품목 표(ExpenseItemPanel)로 이용내역 입력+자동 합산(다른 사업과
// 동일 관례). 일용직급여만 인건비 명단(SalaryPanelIntegrated)을 쓴다.
const ITEM_PANEL_TEMPLATES = ["service_contract", "fuel_cost", "service_contract_payment"];
const SALARY_PANEL_TEMPLATES = ["service_daily_payroll"];

const serviceDraftConfig = makeExpenseDraftConfig({
  namespace: "service-draft",
  category: "용역사업",
  businessLabel: "용역사업",
  // ✅ (구) 페이지가 실제로 쓰는 ServiceExpense.draft_no 접두어와 정확히 일치(DB
  // 실측: 기존 176건 전부 "금천일자리(주)용역지출").
  draftNoPrefix: "금천일자리(주)용역지출",
  dataApiBase: "/expense/service/",
  combinedCreateApi: "/expense/service/draft/",
  newRoutePath: "/expense/service/draft/new",
  listRoutePath: "/expense/service/draft/list",
  previewRoutePath: "/expense/service/draft/preview",
  templates: TEMPLATES,
  categoryItemFieldDefaults: CATEGORY_ITEM_FIELD_DEFAULTS,
  itemPanelTemplates: ITEM_PANEL_TEMPLATES,
  salaryPanelTemplates: SALARY_PANEL_TEMPLATES,
  // ✅ ServiceExpenseSerializer는 category_name/item_name/field_name이 아니라
  // service_type/service_content/service_location으로 노출 — 경영팀과 동일 지점.
  fieldNameMap: { categoryName: "service_type", itemName: "service_content", fieldName: "service_location" },
  VendorPanel,
  ExpenseItemPanel,
  SalaryPanel: SalaryPanelIntegrated,
});

export default serviceDraftConfig;
